/**
 * TicketTierManager - Manage ticket tiers and pricing for an event
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { DateTimePicker } from './TimePicker';
import { Plus, Pencil, Trash2, Ticket, DollarSign, Users, Loader2, Clock, Percent } from 'lucide-react';
import { eventAPI } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getToken } from '@/lib/authToken';

// Tier Form Component
const TierForm = ({ tier, onSubmit, onCancel }) => {
  // Convert benefits array to string for textarea display
  const initialBenefits = tier?.benefits 
    ? (Array.isArray(tier.benefits) ? tier.benefits.join('\n') : tier.benefits)
    : '';
    
  const [form, setForm] = useState({
    name: tier?.name || '',
    price: tier?.price || 0,
    capacity: tier?.capacity || 100,
    description: tier?.description || '',
    benefits: initialBenefits,
    is_visible: tier?.is_visible ?? true,
    color: tier?.color || '#667eea',
    early_bird_price: tier?.early_bird_price || '',
    early_bird_deadline: tier?.early_bird_deadline || '',
    // Access permissions
    meals_included: tier?.meals_included ?? false,
    housing_included: tier?.housing_included ?? false,
    vip_access: tier?.vip_access ?? false,
    networking_access: tier?.networking_access ?? true,
    workshops_access: tier?.workshops_access ?? true,
    materials_access: tier?.materials_access ?? true,
    recording_access: tier?.recording_access ?? false,
    priority_seating: tier?.priority_seating ?? false,
    swag_bag: tier?.swag_bag ?? false,
  });

  // Preset color palette for badge colors
  const colorPresets = [
    { name: 'Indigo', value: '#667eea' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Teal', value: '#14b8a6' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Gold', value: '#eab308' },
    { name: 'Slate', value: '#64748b' },
  ];

  const accessPermissions = [
    { key: 'meals_included', label: 'Meals Included', icon: '🍽️', description: 'Access to event meals' },
    { key: 'housing_included', label: 'Housing Included', icon: '🏨', description: 'Accommodation provided' },
    { key: 'vip_access', label: 'VIP Access', icon: '⭐', description: 'VIP areas and perks' },
    { key: 'networking_access', label: 'Networking', icon: '🤝', description: 'Access to networking events' },
    { key: 'workshops_access', label: 'Workshops', icon: '🎓', description: 'Access to workshops' },
    { key: 'materials_access', label: 'Materials', icon: '📚', description: 'Event materials/downloads' },
    { key: 'recording_access', label: 'Recordings', icon: '🎥', description: 'Access to session recordings' },
    { key: 'priority_seating', label: 'Priority Seating', icon: '💺', description: 'Reserved/front seating' },
    { key: 'swag_bag', label: 'Swag Bag', icon: '🎁', description: 'Conference merchandise' },
  ];

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Tier Name *</Label>
          <Input 
            value={form.name} 
            onChange={e => setForm({...form, name: e.target.value})}
            placeholder="e.g., VIP, Standard, Student"
          />
        </div>
        <div>
          <Label>Price ($)</Label>
          <Input 
            type="number" 
            min="0"
            step="0.01"
            value={form.price} 
            onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Capacity</Label>
          <Input 
            type="number" 
            min="1"
            value={form.capacity} 
            onChange={e => setForm({...form, capacity: parseInt(e.target.value) || 1})}
          />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch 
            checked={form.is_visible} 
            onCheckedChange={v => setForm({...form, is_visible: v})}
          />
          <Label>Visible to public</Label>
        </div>
      </div>

      {/* Badge Color Selection */}
      <div>
        <Label className="mb-2 block">Badge Color</Label>
        <p className="text-xs text-slate-500 mb-3">This color will appear on attendee badges for this tier</p>
        <div className="flex flex-wrap gap-2">
          {colorPresets.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => setForm({...form, color: preset.value})}
              className={`w-10 h-10 rounded-lg border-2 transition-all ${
                form.color === preset.value 
                  ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' 
                  : 'hover:scale-105'
              }`}
              style={{ backgroundColor: preset.value, borderColor: form.color === preset.value ? preset.value : 'transparent' }}
              title={preset.name}
            />
          ))}
          {/* Custom color input */}
          <div className="relative">
            <input
              type="color"
              value={form.color}
              onChange={(e) => setForm({...form, color: e.target.value})}
              className="w-10 h-10 rounded-lg cursor-pointer border-2 border-dashed border-slate-300"
              title="Custom color"
            />
          </div>
        </div>
        {/* Preview Badge */}
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-slate-500">Preview:</span>
          <span 
            className="px-3 py-1 rounded-full text-white text-sm font-medium"
            style={{ backgroundColor: form.color }}
          >
            {form.name || 'Tier Name'}
          </span>
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Textarea 
          value={form.description || ''} 
          onChange={e => setForm({...form, description: e.target.value})}
          placeholder="What's included in this tier..."
          rows={2}
        />
      </div>

      <div>
        <Label>Benefits (one per line)</Label>
        <Textarea 
          value={form.benefits || ''} 
          onChange={e => setForm({...form, benefits: e.target.value})}
          placeholder="Priority seating&#10;Networking dinner&#10;Conference swag bag"
          rows={3}
        />
      </div>

      {/* Access Permissions Section */}
      <div className="border rounded-lg p-4 bg-slate-50">
        <Label className="text-base font-semibold mb-3 block">Access Permissions</Label>
        <p className="text-xs text-slate-500 mb-4">Configure what attendees with this tier can access</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {accessPermissions.map(perm => (
            <div 
              key={perm.key}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                form[perm.key] 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setForm({...form, [perm.key]: !form[perm.key]})}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg">{perm.icon}</span>
                <Switch 
                  checked={form[perm.key]} 
                  onCheckedChange={v => setForm({...form, [perm.key]: v})}
                  onClick={e => e.stopPropagation()}
                />
              </div>
              <div className="text-sm font-medium">{perm.label}</div>
              <div className="text-xs text-slate-500">{perm.description}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Early Bird Price ($)</Label>
          <Input 
            type="number" 
            min="0"
            step="0.01"
            value={form.early_bird_price || ''} 
            onChange={e => setForm({...form, early_bird_price: e.target.value ? parseFloat(e.target.value) : ''})}
            placeholder="Optional"
          />
        </div>
        <div>
          <DateTimePicker
            label="Early Bird Deadline"
            value={form.early_bird_deadline ? form.early_bird_deadline.slice(0, 16) : ''} 
            onChange={v => setForm({...form, early_bird_deadline: v})}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={() => {
            // Transform benefits string to array before submitting
            // Also convert empty strings to null for optional numeric fields
            const submitData = {
              ...form,
              benefits: typeof form.benefits === 'string' 
                ? form.benefits.split('\n').map(b => b.trim()).filter(b => b)
                : form.benefits || [],
              // Convert empty strings to null for optional numeric fields
              early_bird_price: form.early_bird_price === '' || form.early_bird_price === null ? null : parseFloat(form.early_bird_price),
              early_bird_deadline: form.early_bird_deadline || null,
            };
            onSubmit(submitData);
          }} 
          disabled={!form.name}
        >
          {tier ? 'Update' : 'Create'} Tier
        </Button>
      </div>
    </div>
  );
};

const TicketTierManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Late registration settings state
  const [lateSettings, setLateSettings] = useState({
    enabled: false,
    fee_type: 'fixed',
    fee_value: 0,
    start_date: ''
  });
  const [savingLateSettings, setSavingLateSettings] = useState(false);

  const { data: tiers = [], isLoading } = useQuery({
    queryKey: ['tiers', eventId],
    queryFn: () => eventAPI.getAdminTiers(eventId).then(r => {
      const data = r.data;
      if (Array.isArray(data)) return data;
      if (data?.tiers && Array.isArray(data.tiers)) return data.tiers;
      return [];
    }),
  });

  // Fetch late registration settings
  useQuery({
    queryKey: ['late-settings', eventId],
    queryFn: async () => {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/events/${eventId}/settings/late-registration`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data) {
        // Normalize field names for backwards compatibility
        setLateSettings({
          enabled: data.enabled ?? false,
          fee_type: data.fee_type || data.surcharge_type || 'fixed',
          fee_value: data.fee_value ?? data.surcharge_value ?? 0,
          start_date: data.start_date || data.deadline || ''
        });
      }
      return data;
    },
  });

  const saveLateSettings = async () => {
    setSavingLateSettings(true);
    try {
      const token = getToken();
      if (!token) {
        // Try zustand store
        try {
          const authStorage = localStorage.getItem('eventflow-auth');
          if (authStorage) {
            const parsed = JSON.parse(authStorage);
            if (parsed?.state?.token) {
              const storeToken = parsed.state.token;
              const res = await fetch(`${API_URL}/api/events/${eventId}/settings/late-registration`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${storeToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(lateSettings)
              });
              if (res.ok) {
                toast.success('Late registration settings saved');
                queryClient.invalidateQueries(['late-settings', eventId]);
              } else {
                const errData = await res.json().catch(() => ({}));
                toast.error(errData.detail || 'Failed to save settings');
              }
              return;
            }
          }
        } catch (e) {}
        toast.error('Session expired. Please log in again.');
        return;
      }
      const res = await fetch(`${API_URL}/api/events/${eventId}/settings/late-registration`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lateSettings)
      });
      if (res.ok) {
        toast.success('Late registration settings saved');
        queryClient.invalidateQueries(['late-settings', eventId]);
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSavingLateSettings(false);
    }
  };

  // Helper function to extract error message
  const getErrorMessage = (err, defaultMsg) => {
    const data = err.response?.data;
    if (typeof data === 'string') return data;
    if (data?.detail) return typeof data.detail === 'string' ? data.detail : defaultMsg;
    if (Array.isArray(data) && data[0]?.msg) return data[0].msg;
    return defaultMsg;
  };

  const createMutation = useMutation({
    mutationFn: (data) => eventAPI.addTier(eventId, data),
    onSuccess: () => {
      toast.success('Ticket tier created');
      queryClient.invalidateQueries(['tiers', eventId]);
      setIsOpen(false);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create tier')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => eventAPI.updateTier(eventId, id, data),
    onSuccess: () => {
      toast.success('Ticket tier updated');
      queryClient.invalidateQueries(['tiers', eventId]);
      setEditingTier(null);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to update tier')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => eventAPI.deleteTier(eventId, id),
    onSuccess: () => {
      toast.success('Ticket tier deleted');
      queryClient.invalidateQueries(['tiers', eventId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to delete tier')),
  });

  const totalCapacity = tiers.reduce((acc, t) => acc + (t.capacity || 0), 0);
  const totalSold = tiers.reduce((acc, t) => acc + (t.sold || 0), 0);
  const totalRevenue = tiers.reduce((acc, t) => acc + ((t.sold || 0) * (t.price || 0)), 0);

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ticket-tier-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="h-6 w-6" />
            Ticket Tiers
          </h2>
          <p className="text-sm text-slate-500">Manage pricing and capacity for your event</p>
        </div>
        
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-tier-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Tier
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Ticket Tier</DialogTitle>
            </DialogHeader>
            <TierForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">Total Tiers</div>
            <div className="text-2xl font-bold">{tiers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">Total Capacity</div>
            <div className="text-2xl font-bold flex items-center gap-1">
              <Users className="h-5 w-5" />{totalCapacity}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">Tickets Sold</div>
            <div className="text-2xl font-bold">{totalSold}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-sm text-slate-500">Revenue</div>
            <div className="text-2xl font-bold flex items-center gap-1">
              <DollarSign className="h-5 w-5" />{totalRevenue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tiers List */}
      {tiers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Ticket className="h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500">No ticket tiers configured.</p>
            <p className="text-sm text-slate-400">Click "Add Tier" to create your first pricing tier.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tiers.map(tier => (
            <Card key={tier.id} data-testid={`tier-${tier.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {tier.name}
                      {!tier.is_visible && <Badge variant="secondary">Hidden</Badge>}
                    </CardTitle>
                    <div className="text-2xl font-bold mt-1">
                      ${tier.price?.toFixed(2) || '0.00'}
                    </div>
                    {tier.early_bird_price && (
                      <div className="text-sm text-green-600">
                        Early Bird: ${tier.early_bird_price.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditingTier(tier)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(tier.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {tier.description && (
                  <p className="text-sm text-slate-600 mb-3">{tier.description}</p>
                )}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Sold</span>
                    <span className="font-medium">{tier.sold || 0} / {tier.capacity}</span>
                  </div>
                  <Progress value={((tier.sold || 0) / tier.capacity) * 100} className="h-2" />
                </div>
                {tier.benefits && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-slate-500 mb-1">Benefits:</div>
                    <ul className="text-sm text-slate-600 list-disc list-inside">
                      {(Array.isArray(tier.benefits) 
                        ? tier.benefits 
                        : typeof tier.benefits === 'string' 
                          ? tier.benefits.split('\n').filter(b => b.trim())
                          : []
                      ).map((b, i) => (
                        <li key={i}>{typeof b === 'string' ? b.trim() : b}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Access Permissions Display */}
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-slate-500 mb-2">Access Included:</div>
                  <div className="flex flex-wrap gap-1">
                    {tier.meals_included && <Badge variant="outline" className="text-xs">🍽️ Meals</Badge>}
                    {tier.housing_included && <Badge variant="outline" className="text-xs">🏨 Housing</Badge>}
                    {tier.vip_access && <Badge variant="outline" className="text-xs bg-yellow-50">⭐ VIP</Badge>}
                    {tier.networking_access && <Badge variant="outline" className="text-xs">🤝 Networking</Badge>}
                    {tier.workshops_access && <Badge variant="outline" className="text-xs">🎓 Workshops</Badge>}
                    {tier.materials_access && <Badge variant="outline" className="text-xs">📚 Materials</Badge>}
                    {tier.recording_access && <Badge variant="outline" className="text-xs">🎥 Recordings</Badge>}
                    {tier.priority_seating && <Badge variant="outline" className="text-xs">💺 Priority Seating</Badge>}
                    {tier.swag_bag && <Badge variant="outline" className="text-xs">🎁 Swag</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Late Registration Settings */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-amber-600" />
            Late Registration Fee
          </CardTitle>
          <p className="text-sm text-slate-500">
            Charge additional fees for walk-in or late registrations
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="late-enabled" className="cursor-pointer">
              Enable Late Registration Fee
            </Label>
            <Switch
              id="late-enabled"
              checked={lateSettings.enabled}
              onCheckedChange={(checked) => setLateSettings({ ...lateSettings, enabled: checked })}
            />
          </div>

          {lateSettings.enabled && (
            <div className="space-y-4 pt-2 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fee Type</Label>
                  <Select
                    value={lateSettings.fee_type}
                    onValueChange={(v) => setLateSettings({ ...lateSettings, fee_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">
                        <span className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" /> Fixed Amount
                        </span>
                      </SelectItem>
                      <SelectItem value="percentage">
                        <span className="flex items-center gap-2">
                          <Percent className="h-4 w-4" /> Percentage of Ticket
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fee Value {lateSettings.fee_type === 'percentage' ? '(%)' : '($)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    step={lateSettings.fee_type === 'percentage' ? '1' : '0.01'}
                    value={lateSettings.fee_value || ''}
                    onChange={(e) => setLateSettings({ ...lateSettings, fee_value: parseFloat(e.target.value) || 0 })}
                    placeholder={lateSettings.fee_type === 'percentage' ? 'e.g., 25' : 'e.g., 50.00'}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Late Registration Starts (optional)</Label>
                <Input
                  type="datetime-local"
                  value={lateSettings.start_date ? lateSettings.start_date.slice(0, 16) : ''}
                  onChange={(e) => setLateSettings({ ...lateSettings, start_date: e.target.value })}
                />
                <p className="text-xs text-slate-500">
                  If set, late fees only apply after this date. Leave empty to always apply manually.
                </p>
              </div>

              {/* Preview */}
              <div className="bg-white p-3 rounded-lg border">
                <p className="text-sm font-medium mb-1">Preview:</p>
                <p className="text-sm text-slate-600">
                  {lateSettings.fee_type === 'fixed' 
                    ? `Late registrants will pay an additional $${lateSettings.fee_value?.toFixed(2) || '0.00'}`
                    : `Late registrants will pay an additional ${lateSettings.fee_value || 0}% of their ticket price`
                  }
                </p>
                {lateSettings.fee_type === 'percentage' && tiers.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Example: {tiers[0].name} (${tiers[0].price}) → Late fee: ${((tiers[0].price * lateSettings.fee_value) / 100).toFixed(2)}
                  </p>
                )}
              </div>

              <Button 
                onClick={saveLateSettings}
                disabled={savingLateSettings}
                className="w-full"
              >
                {savingLateSettings ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Late Registration Settings'
                )}
              </Button>
            </div>
          )}

          {/* Save button always visible so toggling off can be saved too */}
          {!lateSettings.enabled && (
            <Button 
              onClick={saveLateSettings}
              disabled={savingLateSettings}
              variant="outline"
              className="w-full mt-2"
            >
              {savingLateSettings ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingTier} onOpenChange={(open) => !open && setEditingTier(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Ticket Tier</DialogTitle>
          </DialogHeader>
          {editingTier && (
            <TierForm 
              tier={editingTier} 
              onSubmit={data => updateMutation.mutate({ id: editingTier.id, data })} 
              onCancel={() => setEditingTier(null)} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketTierManager;
